import '@/components/atoms/layout/CMSBlogLayout.css';

import { getText } from '@/lib/i18n/i18n.server';
import { cn } from '@/lib/utils';

import { RichTextFieldSBProps } from '@/components/atoms/richTextField/RichTextFieldSB';
import { CMSComponent } from '@/components/cms/CMSComponent';
import { DecodoComponentsMap } from '@/components/cms/components-decodo-list';
import { Entry } from '@/components/cms/types/entry';

import FooterStrapi from '@/decodo/blocks/Footer/FooterStrapi';
import { Divider as DividerDC } from '@/decodo/components/atoms/Divider';
import { AuthorStrapi as AuthorStrapiDC } from '@/decodo/components/organisms/author/AuthorStrapi';
import { LeadPopUpModal } from '@/decodo/components/organisms/LeadPopUp/LeadPopUpModal';
import { SideNavigationStrapiDC } from '@/decodo/features/blog/SideNavigation/SideNavigationStrapi';
import { HeaderStrapi } from '@/decodo/features/Header/HeaderStrapi';
import { BlogMetadataStrapiDC } from '@/decodo/features/shared/BlogMetaData/BlogMetaDataStrapi';
import { getRichTextFieldHeadingLevel2 } from '@/features/blog/SideNavigation/utils';
import { BlockHeader } from '@/features/utils/BlockHeaderContent';

import Socials from './socials/Socials';

type CMSBlogLayoutProps = {
  entry: Entry;
};

export const gatedContentRootId = 'gated-content-root';
export const blogContentId = 'blog-main-content';

function seperateBlogBlocks(blocks: Entry['blocks']) {
  const asideBlocksNames = ['blog.blog-side-banner'];
  const asideBlocks = blocks.filter((block) =>
    asideBlocksNames.includes(block.__component)
  );

  const fullwidthBlocksNames = [
    'accordions.faq',
    'blog.related-articles',
    'layout.footer-banner',
  ];
  const fullwidthBlocks = blocks.filter((block) =>
    fullwidthBlocksNames.includes(block.__component)
  );

  const contentBlocks = blocks.filter(
    (block) =>
      !asideBlocksNames.includes(block.__component) &&
      !fullwidthBlocksNames.includes(block.__component)
  );

  return { asideBlocks, fullwidthBlocks, contentBlocks };
}

export const GatedContentTargetContainer = () => {
  return <div id={gatedContentRootId} className='w-full' />;
};

export default async function CMSBlogLayout({ entry }: CMSBlogLayoutProps) {
  const { header_reference, footer_reference, seo, metadata, lead_pop_up } =
    entry;
  const texts = await getText();

  const { structuredData } = seo ?? {};

  const { asideBlocks, contentBlocks, fullwidthBlocks } = seperateBlogBlocks(
    entry.blocks
  );

  const headerTitles: string[] = contentBlocks.reduce((acc, block) => {
    if (!!block?.text) {
      acc.push(
        ...getRichTextFieldHeadingLevel2(block.text as RichTextFieldSBProps)
      );
    }
    if (!!(block?.header as BlockHeader)?.title) {
      const title = (block.header as BlockHeader).title;
      if (typeof title === 'string') {
        acc.push(title);
      }
    }
    return acc;
  }, [] as string[]);

  const showSideNavigation = headerTitles.length > 1 || asideBlocks.length > 0;

  return (
    <>
      {header_reference?.data?.attributes && (
        <HeaderStrapi block={header_reference.data.attributes} />
      )}
      {entry.blocks && (
        <main id='main' className={cn('flex-grow pt-0 md:pt-0')}>
          <div className='flex w-full flex-col justify-center  pb-6 md:flex-row'>
            <div
              className={cn(
                ' mt-8 flex flex-col items-center md:mt-10',
                !showSideNavigation
                  ? 'max-w-[calc(928px_+_192px)] md:w-full'
                  : 'max-w-[calc(600px_+_124px)]',
                'narrowPageLayout isDecodo'
              )}
              id={blogContentId}
            >
              <BlogMetadataStrapiDC block={metadata} />

              {contentBlocks.map((block) => (
                <CMSComponent
                  key={block.id}
                  block={block}
                  componentsList={DecodoComponentsMap}
                />
              ))}
              {metadata?.author?.data && (
                <AuthorStrapiDC block={{ blogAuthor: metadata.author }} />
              )}
            </div>

            {showSideNavigation && (
              <>
                <div className='hidden md:block'>
                  <DividerDC
                    alignment='vertical'
                    classNames={{ root: 'border-gray-200' }}
                  />
                </div>
                <div
                  id='blog-aside-content'
                  className='blogAsideContent sticky top-16 hidden h-fit max-h-[calc(100vh_-_4rem)] w-full max-w-[calc(328px_+_68px)] flex-col pl-0 md:flex md:gap-6 md:pb-10 md:pl-12 md:pr-5 md:pt-10'
                >
                  <SideNavigationStrapiDC
                    title={texts.blog.in_this_article}
                    formattedData={headerTitles}
                  />
                  {asideBlocks.length > 0 && (
                    <DividerDC
                      alignment='horizontal'
                      classNames={{ root: 'border-gray-200' }}
                    />
                  )}
                  {asideBlocks.map((block) => (
                    <CMSComponent
                      key={block.id}
                      block={block}
                      componentsList={DecodoComponentsMap}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          <GatedContentTargetContainer />
          {lead_pop_up?.data?.attributes && (
            <LeadPopUpModal
              lead_pop_up={lead_pop_up}
              urlHash={lead_pop_up.data.attributes.urlHash}
            />
          )}

          <div className='flex w-full flex-col'>
            <>
              {fullwidthBlocks.map((block, index) => (
                <CMSComponent
                  key={`${block.id}${index}`}
                  block={block}
                  componentsList={DecodoComponentsMap}
                />
              ))}
            </>
          </div>
        </main>
      )}
      {footer_reference?.data?.attributes && (
        <div className='dark'>
          <FooterStrapi data={footer_reference.data.attributes} />
        </div>
      )}
      {!!structuredData && (
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(structuredData),
          }}
        />
      )}
      <Socials />
    </>
  );
}
